import { useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { MRT_SortingState } from "material-react-table";
import { mrtSortingStateToObject } from "src/api/apiHelpers";

import { z } from "zod";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { fetchAllHydraMembers } from "src/api/_common/hydra/fetchAllHydraMembers";
import { useFoodpilotContext } from "src/context/FoodpilotContext";

type Options = {
    page?: number;
    itemsPerPage?: number;
    search?: string;
    sorting?: MRT_SortingState;
    enabled?: boolean;
    fetchAllPages?: boolean;
};

type ParamsType = {
    params: {
        page: number;
        itemsPerPage: number;
        q: string;
        order?: Record<string, string>;
        organization_id: number;
    };
};

export const allIngredientReferencesKey = "allIngredientReferences" as const;

export const useAllIngredientReferences = (options?: Options) => {
    const {
        page = 1,
        itemsPerPage = 200,
        search = "",
        sorting = [],
        enabled = true,
        fetchAllPages = false,
    } = options || {};
    const { selectedCompany } = useFoodpilotContext();
    const organizationId = selectedCompany?.id;
    const isEnabled = enabled && organizationId !== undefined;
    const order = mrtSortingStateToObject(sorting);

    const query = useQuery({
        queryKey: [
            allIngredientReferencesKey,
            organizationId ?? null,
            isEnabled,
            page,
            itemsPerPage,
            search,
            sorting,
            fetchAllPages,
        ],
        enabled: isEnabled,
        keepPreviousData: false,
        queryFn: async () => {
            if (!enabled || organizationId === undefined) {
                throw new Error("Cannot load ingredient references while disabled or without a selected organization");
            }

            if (fetchAllPages) {
                const flattenedOrder = Object.fromEntries(
                    Object.entries(order).map(([field, direction]) => [`order[${field}]`, direction]),
                );
                const members = await fetchAllHydraMembers({
                    client: loggedInApiCall,
                    endpoint: "/api/ingredient-references",
                    itemsPerPage: 100,
                    params: {
                        organization_id: organizationId,
                        q: search !== "" ? search : undefined,
                        ...flattenedOrder,
                    },
                });
                const items = zodParseWithLog(
                    members,
                    IngredientReferenceSchemaZod.array(),
                    allIngredientReferencesKey,
                );

                return { items, totalItems: items.length };
            }

            const params: ParamsType = {
                params: {
                    page,
                    itemsPerPage,
                    q: search,
                    order,
                    organization_id: organizationId,
                },
            };
            const request = await loggedInApiCall.get("/api/ingredient-references", params);
            const totalItems = request.data["hydra:totalItems"];
            const members = request.data["hydra:member"];

            const items = zodParseWithLog(members, IngredientReferenceSchemaZod.array(), allIngredientReferencesKey);

            return { items, totalItems };
        },
    });
    const data = isEnabled ? query.data : undefined;

    return {
        ...query,
        data,
        ingredientReferences: data?.items,
        totalItems: data?.totalItems,
    };
};

export type IngredientReferenceScore = {
    "@id"?: IRI;
    id?: number;
    scoreId: number;
    value: number | null;
    /**
     * Campaign IRI (e.g. `/api/campaigns/2`). Versioning per Campaign is mandatory:
     * historical reports MUST NOT change when a supplier updates its emission factors
     * for a future campaign. The grid in the IngredientReference settings screen renders
     * one column per Campaign × one row per scoreId; payloads MUST round-trip every cell
     * to avoid orphaning past-campaign rows on PUT.
     */
    campaign: IRI;
};

export type IngredientReferenceCompanyLine = {
    "@id": IRI;
    id: number;
    name: string;
};

export type IngredientReferenceCompanyAssociation = {
    "@id"?: IRI;
    id?: number;
    organization: IngredientReferenceCompanyLine;
};

export type IngredientReference = {
    "@id": IRI;
    id: number;
    name: string;
    code?: string | null;
    description?: string | null;
    cpcCode?: string | null;
    defaultOrigin?: string | null;
    organic?: boolean | null;
    fresh?: boolean | null;
    animal?: boolean | null;
    scores: IngredientReferenceScore[];
    /**
     * Owning company. The backend forces it to the user's active company on POST and
     * never reassigns it on PUT — only the owner is allowed to widen visibility via
     * `ingredientReferenceOrganizations`. Nullable to tolerate platform-level seeds whose
     * `ownedBy` may be absent (the backend filter still surfaces them for everyone).
     */
    ownedBy?: IngredientReferenceCompanyLine | null;
    /**
     * Explicit visibility model: every additional company that should see this
     * reference is materialized here as a junction row. Empty array = visible only
     * by `ownedBy` + platform-owned references. Mirrors the `IngredientCompany`
     * pattern on `Ingredient` (see `AssociatedStructuresField`).
     */
    ingredientReferenceOrganizations: IngredientReferenceCompanyAssociation[];
};

export const IngredientReferenceScoreSchemaZod: z.ZodSchema<IngredientReferenceScore> = z.object({
    "@id": z.string().optional(),
    id: z.number().optional(),
    scoreId: z.number(),
    value: z.number().nullable().optional().default(null),
    campaign: z.string(),
});

export const IngredientReferenceCompanyLineSchemaZod: z.ZodSchema<IngredientReferenceCompanyLine> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
});

export const IngredientReferenceCompanyAssociationSchemaZod: z.ZodSchema<IngredientReferenceCompanyAssociation> =
    z.object({
        "@id": z.string().optional(),
        id: z.number().optional(),
        organization: IngredientReferenceCompanyLineSchemaZod,
    });

export const IngredientReferenceSchemaZod: z.ZodSchema<IngredientReference> = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    code: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    cpcCode: z.string().nullable().optional(),
    defaultOrigin: z.string().nullable().optional(),
    organic: z.boolean().nullable().optional(),
    fresh: z.boolean().nullable().optional(),
    animal: z.boolean().nullable().optional(),
    scores: IngredientReferenceScoreSchemaZod.array(),
    // The API serializer omits `ownedBy` when null on legacy rows; tolerate `undefined`
    // here so the grid does not error out before the user has migrated old fixtures.
    ownedBy: IngredientReferenceCompanyLineSchemaZod.nullable().optional(),
    // `default([])` keeps older API responses (without the field) parseable while the
    // backend rolls out — once the migration is applied, the field is always present.
    ingredientReferenceOrganizations: IngredientReferenceCompanyAssociationSchemaZod.array().default([]),
});
